// src/platform/AiDashboardsPage.jsx
//
// The "AI Dashboards" section of the platform admin UI.
//
// Two states in one page: the card grid, and one open dashboard. It keeps the
// selection in local state rather than the router, matching how every other
// platform-admin section works — PlatformAdminLayout holds `active` in useState
// and PlatformAdminContent switches on it, with no URL involved. Introducing a
// route for this one section would make it the only part of the admin UI whose
// place is addressable, which is a decision for the whole shell, not for one
// panel. The trade is that a dashboard cannot be linked to or survive a reload;
// noted here so the next person sees it was chosen.
//
// DYNAMIC REPOPULATION. Opening a card mounts the dashboard component fresh,
// and that component fetches its own detail payload — the grid's rollup is
// never passed down as a seed. So an open dashboard always shows the corpus as
// it is at open time, and returning to the grid refetches the rollups. The
// corpus is genuinely live (analysts commit continuously), so a cached first
// paint would be wrong within seconds.

import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, ChevronLeft, RefreshCw } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import AiDashboardCard from "./dashboards/AiDashboardCard";
import { resolveDashboard } from "./dashboards/dashboardRegistry";

export default function AiDashboardsPage() {
  const [openKey, setOpenKey] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth("/api/v1/platform/ai-dashboards");
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = await res.json();
      setCards(body.dashboards ?? []);
    } catch (err) {
      setError(err.message || "Failed to load dashboards");
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch whenever we return to the grid, so the rollups a user comes back to
  // are not the ones they left. `openKey` is the dependency precisely because
  // the transition back is the moment the numbers are stale.
  useEffect(() => { if (openKey === null) loadCards(); }, [openKey, loadCards]);

  // ── one dashboard ──────────────────────────────────────────────────────────
  if (openKey !== null) {
    const card = cards.find((c) => c.key === openKey);
    const Dashboard = resolveDashboard(openKey);

    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-bg-border shrink-0">
          <button
            onClick={() => setOpenKey(null)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded
                       border border-bg-border bg-bg-soft hover:bg-bg-softer
                       text-fg-secondary hover:text-fg-primary transition"
          >
            <ChevronLeft size={13} />
            Dashboards
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-fg-primary truncate">
              {card?.title ?? openKey}
            </h1>
            {card?.subtitle ? (
              <p className="text-[11px] text-fg-secondary truncate">{card.subtitle}</p>
            ) : null}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <Dashboard dashboardKey={openKey} title={card?.title} />
        </div>
      </div>
    );
  }

  // ── the grid ───────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-bg-border shrink-0">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-5 h-5 text-accent-action" />
          <h1 className="text-lg font-semibold text-fg-primary">AI Dashboards</h1>
        </div>
        <button
          onClick={loadCards}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded
                     border border-bg-border bg-bg-soft hover:bg-bg-softer
                     text-fg-secondary hover:text-fg-primary transition disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5">
        {error ? (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 max-w-xl">
            <div className="text-sm text-red-300 font-medium">Could not load dashboards</div>
            <div className="text-xs text-fg-secondary mt-1">{error}</div>
            <button onClick={loadCards}
                    className="mt-3 text-xs px-3 py-1.5 rounded border border-bg-border
                               bg-bg-soft hover:bg-bg-softer text-fg-primary">
              Retry
            </button>
          </div>
        ) : loading && cards.length === 0 ? (
          <div className="text-sm text-fg-secondary">Loading dashboards…</div>
        ) : cards.length === 0 ? (
          <div className="text-sm text-fg-secondary">No dashboards are registered yet.</div>
        ) : (
          <>
            <p className="text-xs text-fg-secondary mb-4 max-w-2xl leading-relaxed">
              Operational analytics across AI products. Figures on each card are rollups read
              at page load; opening a dashboard fetches its full detail fresh.
            </p>

            {/* auto-fill, not a fixed column count — the grid is expected to grow,
                and a hardcoded 3-up would leave a lone card stretched across a row. */}
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))" }}
            >
              {cards.map((card) => (
                <AiDashboardCard key={card.key} card={card} onOpen={setOpenKey} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
